import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  InputAdornment,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Download,
  Refresh,
  MoreVert,
  Email,
  Chat,
  WhatsApp,
  CheckCircle,
  Error,
  SmartToy,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { RootState } from '../store/index';
import { apiService } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { FiltroPorPeriodo, FiltroPorPeriodoValue } from '../components/FiltroPorPeriodo';
import { ActiveFiltersBar } from '../components/ActiveFiltersBar';
import { useActiveFilters } from '../hooks/useActiveFilters';
import { buildBotExecutionDetail, buildBotExecutionSummary } from '../utils/botExecutionLabels';
import { getDefaultPeriodRange } from '../utils/defaultDateRange';
import { formatOfficialDateBR } from '../utils/officialDate';

type LogType = 'deliveries' | 'bot';

type UnifiedLog =
  | {
      kind: 'delivery';
      id: number;
      channel: string;
      frequency: string;
      status: string;
      at: string;
      detail?: string;
    }
  | {
      kind: 'bot';
      id: number;
      channel: string;
      frequency: string;
      status: string;
      at: string;
      detail?: string;
      execucaoId: number;
    };

export function LogsPage() {
  const theme = useTheme();
  const { user } = useSelector((state: RootState) => state.auth);
  const [logType, setLogType] = useState<LogType>('bot');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [periodFilter, setPeriodFilter] = useState<FiltroPorPeriodoValue>(getDefaultPeriodRange);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLog, setSelectedLog] = useState<UnifiedLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsText, setDetailsText] = useState<string>('');

  const { data: deliveryLogsData, isLoading: deliveriesLoading, error: deliveriesError, refetch: refetchDeliveries } =
    useQuery(
      ['delivery-logs', user?.id, searchQuery, statusFilter, channelFilter, periodFilter, page, rowsPerPage],
      () =>
        apiService.getDeliveryLogs({
          user_id: user?.id,
          status: statusFilter || undefined,
          channel: channelFilter || undefined,
          date_start: periodFilter.start,
          date_end: periodFilter.end,
          page: page + 1,
          page_size: rowsPerPage,
        }),
      {
        keepPreviousData: true,
        enabled: logType === 'deliveries',
      }
    );

  const { data: botData, isLoading: botLoading, error: botError, refetch: refetchBot } = useQuery(
    ['bot-execucoes', searchQuery, statusFilter, periodFilter, page, rowsPerPage],
    () =>
      apiService.getBotExecutions({
        fonte_url: 'https://www.gov.br/mma/pt-br/centrais-de-conteudo/legislacao',
        date_start: periodFilter.start,
        date_end: periodFilter.end,
        status: statusFilter || undefined,
        page: page + 1,
        page_size: rowsPerPage,
      }),
    {
      keepPreviousData: true,
      enabled: logType === 'bot',
    }
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleLogTypeChange = (event: { target: { value: unknown } }) => {
    setLogType(event.target.value as LogType);
    setSearchQuery('');
    setStatusFilter('');
    setChannelFilter('');
    setPeriodFilter(getDefaultPeriodRange());
    setPage(0);
  };

  const handleLimparFiltros = () => {
    setSearchQuery('');
    setStatusFilter('');
    setChannelFilter('');
    setPeriodFilter(getDefaultPeriodRange());
    setPage(0);
  };

  const handlePeriodChange = (value: FiltroPorPeriodoValue) => {
    setPeriodFilter(value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleChannelFilterChange = (event: any) => {
    setChannelFilter(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, log: UnifiedLog) => {
    setAnchorEl(event.currentTarget);
    setSelectedLog(log);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLog(null);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export logs');
  };

  const handleSeedData = async () => {
    try {
      const response = await apiService.api.post('/delivery-logs/seed-data');
      if (response.data) {
        refetchDeliveries();
        // Show success message
        alert('Dados de teste criados com sucesso! ' + response.data.records_created + ' logs foram adicionados.');
      }
    } catch (error) {
      console.error('Error seeding data:', error);
      alert('Erro ao criar dados de teste.');
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'mma-powerbi':
        return <SmartToy />;
      case 'email':
        return <Email />;
      case 'slack':
        return <Chat />;
      case 'whatsapp':
        return <WhatsApp />;
      default:
        return <Email />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'email':
        return 'primary';
      case 'slack':
        return 'secondary';
      case 'whatsapp':
        return 'success';
      default:
        return 'default';
    }
  };

  const getChannelLabel = (channel: string) => {
    switch (channel) {
      case 'mma-powerbi':
        return 'MMA Power BI (BOT)';
      case 'email':
        return 'E-mail';
      case 'slack':
        return 'Slack';
      case 'whatsapp':
        return 'WhatsApp';
      default:
        return channel;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'executed':
        return 'success';
      case 'incomplete':
        return 'warning';
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'executed':
        return 'Executado';
      case 'incomplete':
        return 'Incompleto';
      case 'running':
        return 'Em execução';
      case 'sent':
        return 'Enviado';
      case 'failed':
        return 'Falhou';
      default:
        return status;
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'Diário';
      case 'weekly':
        return 'Semanal';
      case 'monthly':
        return 'Mensal';
      default:
        return frequency;
    }
  };

  const unifiedLogs = useMemo<UnifiedLog[]>(() => {
    if (logType === 'deliveries') {
      const rows = deliveryLogsData?.data ?? [];
      return rows
        .filter((r) => (searchQuery ? (r.detail ?? '').toLowerCase().includes(searchQuery.toLowerCase()) : true))
        .map((r) => ({
          kind: 'delivery' as const,
          id: r.id,
          channel: r.channel,
          frequency: r.frequency,
          status: r.status,
          at: r.sent_at,
          detail: r.detail,
        }));
    }

    const items = botData?.items ?? [];
    const mapStatus = (s: string) => {
      if (s === 'SUCESSO') return 'executed';
      if (s === 'SUCESSO_COM_ALERTAS') return 'incomplete';
      if (s === 'INICIADO') return 'running';
      return 'failed';
    };

    return items
      .filter((r) => {
        if (!searchQuery) return true;
        const hay = `${r.status} ${r.tipo_execucao} ${r.origem_disparo ?? ''}`.toLowerCase();
        return hay.includes(searchQuery.toLowerCase());
      })
      .map((r) => ({
          kind: 'bot' as const,
          id: r.id,
          execucaoId: r.id,
          channel: 'mma-powerbi',
          frequency: 'daily',
          status: mapStatus(String(r.status)),
          at: r.inicio_execucao,
          detail: buildBotExecutionSummary(r),
        }));
  }, [
    logType,
    deliveryLogsData,
    botData,
    searchQuery,
  ]);

  const activeFilters = useActiveFilters({
    searchQuery,
    dateRange: { start: periodFilter.start, end: periodFilter.end },
    onClearSearch: () => setSearchQuery(''),
    onClearDateStart: () => setPeriodFilter(getDefaultPeriodRange()),
    onClearDateEnd: () => setPeriodFilter(getDefaultPeriodRange()),
  });

  const extraActiveFilters = useMemo(() => {
    const items = [...activeFilters];
    if (statusFilter) {
      items.push({
        key: 'status',
        label: `Status: ${getStatusLabel(statusFilter)}`,
        onRemove: () => setStatusFilter(''),
      });
    }
    if (channelFilter) {
      items.push({
        key: 'channel',
        label: `Canal: ${getChannelLabel(channelFilter)}`,
        onRemove: () => setChannelFilter(''),
      });
    }
    return items;
  }, [activeFilters, statusFilter, channelFilter]);

  const totalLogs = logType === 'deliveries' ? (deliveryLogsData?.total || 0) : (botData?.total || 0);
  const sentLogs = unifiedLogs.filter((log) => log.status === 'sent' || log.status === 'executed').length;
  const failedLogs = unifiedLogs.filter((log) => log.status === 'failed').length;
  const successRate = totalLogs > 0 ? Math.round((sentLogs / totalLogs) * 100) : 0;

  const isLoading = logType === 'deliveries' ? deliveriesLoading : botLoading;
  const error = logType === 'deliveries' ? deliveriesError : botError;
  const refetch = logType === 'deliveries' ? refetchDeliveries : refetchBot;

  if (isLoading) {
    return <LoadingSpinner message="Carregando logs..." />;
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="error">
          Erro ao carregar logs de envio
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Logs de Envio
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {logType === 'deliveries' && totalLogs === 0 && (
              <Button
                variant="outlined"
                onClick={handleSeedData}
                sx={{ mr: 1 }}
              >
                Gerar Dados de Teste
              </Button>
            )}
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Enviados
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {sentLogs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Error color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Falharam
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {failedLogs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Taxa de Sucesso
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {successRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {totalLogs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtros — padrão PV (FiltroPorPeriodo + barra compacta) */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <FiltroPorPeriodo
              value={periodFilter}
              onChange={handlePeriodChange}
              label="FILTRAR POR:"
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel shrink>Tipo</InputLabel>
              <Select
                value={logType}
                onChange={handleLogTypeChange}
                label="Tipo"
                displayEmpty
              >
                <MenuItem value="bot">BOT (automação)</MenuItem>
                <MenuItem value="deliveries">Notificações</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Buscar logs..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ minWidth: 200, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="text.secondary">⌕</Typography>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel shrink>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
                displayEmpty
              >
                <MenuItem value="">Todos</MenuItem>
                {logType === 'deliveries' ? (
                  <>
                    <MenuItem value="sent">Enviado</MenuItem>
                    <MenuItem value="failed">Falhou</MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem value="executed">Executado</MenuItem>
                    <MenuItem value="incomplete">Incompleto</MenuItem>
                    <MenuItem value="failed">Falhou</MenuItem>
                    <MenuItem value="running">Em execução</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            {logType === 'deliveries' && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel shrink>Canal</InputLabel>
                <Select
                  value={channelFilter}
                  onChange={handleChannelFilterChange}
                  label="Canal"
                  displayEmpty
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="email">E-mail</MenuItem>
                  <MenuItem value="slack">Slack</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button size="small" variant="outlined" onClick={handleLimparFiltros}>
              Limpar
            </Button>
            <Box sx={{ flex: 1, minWidth: 0 }} />
            <Tooltip title="Atualizar">
              <IconButton size="small" onClick={() => refetch()}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Exportar">
              <IconButton size="small" onClick={handleExport}>
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        <ActiveFiltersBar
          filters={extraActiveFilters.map((f) => ({
            ...f,
            icon: f.key.startsWith('date') ? 'calendar' : f.key === 'search' ? 'search' : 'default',
          }))}
          onClearAll={handleLimparFiltros}
        />

        {/* Results Summary */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {totalLogs} logs encontrados
            {periodFilter.start && periodFilter.end
              ? ` • período ${formatOfficialDateBR(periodFilter.start)} – ${formatOfficialDateBR(periodFilter.end)}`
              : ''}
          </Typography>
        </Box>

        {/* Logs Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Canal</TableCell>
                  <TableCell>Frequência</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Detalhes</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unifiedLogs.length > 0 ? (
                  unifiedLogs.map((log) => (
                    <TableRow key={`${log.kind}-${log.id}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getChannelIcon(log.channel)}
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                            {getChannelLabel(log.channel)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getFrequencyLabel(log.frequency)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(log.status)}
                          size="small"
                          color={getStatusColor(log.status) as any}
                          icon={log.status === 'sent' || log.status === 'executed' ? <CheckCircle /> : <Error />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.at).toLocaleDateString('pt-BR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(log.at).toLocaleTimeString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {log.detail || 'Nenhum detalhe disponível'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, log)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Nenhum log de envio encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {logType === 'bot'
                            ? 'Não há execuções do BOT registradas ainda.'
                            : 'Não há registros de envio de notificações ainda.'}
                        </Typography>
                        {logType === 'deliveries' && (
                          <Button variant="contained" onClick={handleSeedData}>
                            Gerar Dados de Teste
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalLogs}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={async () => {
              try {
                if (!selectedLog) return;
                if (selectedLog.kind === 'bot') {
                  const detail = await apiService.getBotExecution(selectedLog.execucaoId);
                  setDetailsText(buildBotExecutionDetail(detail));
                } else {
                  setDetailsText(selectedLog.detail || 'Nenhum detalhe disponível');
                }
                setDetailsOpen(true);
              } finally {
                handleMenuClose();
              }
            }}
          >
            Ver detalhes
          </MenuItem>
          {selectedLog?.kind === 'bot' && (
            <MenuItem
              onClick={async () => {
                try {
                  const resp = await apiService.triggerBotRun({ mode: 'incremental' });
                  alert(resp?.message || 'BOT disparado com sucesso.');
                  refetch();
                } catch (e) {
                  console.error(e);
                  alert('Falha ao disparar o BOT.');
                } finally {
                  handleMenuClose();
                }
              }}
            >
              Forçar execução agora
            </MenuItem>
          )}
        </Menu>

        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Detalhes</DialogTitle>
          <DialogContent dividers>
            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
              {detailsText}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}
